import React, { useCallback, useState } from 'react';
import { useTranslation } from '@wix/fe-essentials-standalone';
import {
  Box,
  TextButton,
  Loader,
  InputStatus,
  InputArea,
  Button,
} from '@wix/design-system';
import { ArrowDownRightSmall } from '@wix/wix-ui-icons-common';
import { ReviewData } from '@common/types';

const WriteReplyReview = ({
  review,
  writeReplyToReview,
}: {
  review: ReviewData;
  writeReplyToReview: (
    id: string,
    replyBody: string,
    revision: Long.Long | undefined,
  ) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [isInProceess, setIsInProceess] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [replyBody, setReplyBody] = useState<string>('');
  const [replyBodyErrorStatus, setReplyBodyErrorStatus] = useState<
    InputStatus | undefined
  >(undefined);
  const [replyBodyErrorText, setReplyBodyErrorText] = useState<string>('');

  const writeReplyToReviewAndUpdateReviews = useCallback(async () => {
    setIsInProceess(true);
    setReplyBodyErrorStatus(undefined);
    if (replyBody) {
      try {
        await writeReplyToReview(review.id!, replyBody, review.revision);
        setShowForm(false);
        setIsInProceess(false);
      } catch (e: any) {
        setIsInProceess(false);
        setReplyBodyErrorStatus('error');
        setReplyBodyErrorText(
          t('profile-page-review-write-reply-error-server'),
        );
      }
    } else {
      setIsInProceess(false);
      setReplyBodyErrorStatus('error');
      setReplyBodyErrorText(
        t('profile-page-review-write-reply-error-text-not-valid'),
      );
    }
  }, [replyBody, review.id, review.revision, t, writeReplyToReview]);

  return (
    <Box width="100%" dataHook="write-reply-review-container">
      <Box width="SP3">
        <ArrowDownRightSmall />
      </Box>
      {showForm ? (
        <Box
          dataHook="write-reply-review-form"
          display="block"
          width="100%"
          marginLeft="SP2"
        >
          <InputArea
            dataHook="write-reply-review-textarea"
            rows={6}
            placeholder={t('profile-page-review-write-reply-placeholder')}
            onChange={(e: any) => {
              setReplyBodyErrorStatus(undefined);
              setReplyBody(e.target.value);
            }}
            status={replyBodyErrorStatus}
            statusMessage={replyBodyErrorText}
            resizable
          />
          <Box marginTop="SP3">
            <Box marginRight="SP2" marginLeft="auto">
              <Button
                dataHook="write-reply-review-cancel-button"
                size="small"
                priority="secondary"
                onClick={() => setShowForm(false)}
              >
                {t('profile-page-review-write-reply-cancel-button')}
              </Button>
            </Box>
            <Button
              dataHook="write-reply-review-submit-button"
              size="small"
              onClick={writeReplyToReviewAndUpdateReviews}
              disabled={isInProceess}
            >
              {isInProceess ? (
                <Loader size="tiny" />
              ) : (
                t('profile-page-review-write-reply-submit-button')
              )}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box display="block" marginLeft="SP2">
          <Box display="block" marginBottom="SP1">
            <TextButton
              skin="dark"
              dataHook="write-reply-review-link"
              underline="always"
              size="small"
              weight="thin"
              onClick={() => setShowForm(true)}
            >
              {t('profile-page-review-write-reply-link')}
            </TextButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WriteReplyReview;
